import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { authFetch } from "../utils/authFetch";
import { describeAccountAccess, type AccountAccessRow } from "./audience";

interface AccountAccess {
  kind: "internal" | "external" | "service";
  superAdmin: boolean;
  projects: Record<string, AccountAccessRow>;
}

/** Settings → Users → Access: what one account can open, and why — the
 *  server's resolver answer (GET /api/admin/users/{id}/project-access), never
 *  re-derived here. Read-only; Change goes to the editor that owns it. */
export default function AccountAccessPanel({ userId, onChangeRoles }: {
  userId: string;
  /** Joveo people and integrations: switch to the Roles editor. */
  onChangeRoles?: () => void;
}) {
  const [data, setData] = useState<AccountAccess | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setData(null); setFailed(false);
    (async () => {
      try {
        const r = await authFetch(`/api/admin/users/${userId}/project-access`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (r.ok) setData(await r.json()); else setFailed(true);
      } catch (e) {
        if ((e as Error).name !== "AbortError") setFailed(true);
      }
    })();
    return () => ctrl.abort();
  }, [userId]);

  if (failed) return <div className="py-6 text-center text-xs text-red-600">Couldn't load their access.</div>;
  if (!data) return <div className="py-6 text-center text-xs text-slate-400">Loading…</div>;

  const rows = Object.entries(data.projects)
    .map(([id, r]) => ({ id, ...r }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const can = rows.filter(r => r.canOpen);
  const cannot = rows.filter(r => !r.canOpen);

  const change = (r: AccountAccessRow) => data.kind === "external"
    ? <Link to={`/studio/settings/external-users?open=${encodeURIComponent(userId)}`}
        aria-label={`Change access to ${r.name}`}
        className="text-[11px] text-indigo-600 hover:underline shrink-0">Change</Link>
    // An integration's access is the API Access page's, not a role.
    : onChangeRoles && !data.superAdmin && data.kind !== "service"
      ? <button type="button" onClick={onChangeRoles} aria-label={`Change role on ${r.name}`}
          className="text-[11px] text-indigo-600 hover:underline shrink-0">Change</button>
      : null;

  const row = (r: AccountAccessRow & { id: string }) => (
    <li key={r.id} className="flex items-center gap-3 px-3 py-2">
      {r.canOpen
        ? <Check className="size-4 text-emerald-600 shrink-0" aria-label="Can open" />
        : <X className="size-4 text-slate-300 shrink-0" aria-label="Can't open" />}
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] font-medium truncate ${r.canOpen ? "text-slate-800" : "text-slate-500"}`}>{r.name}</div>
        <div className="text-[11px] text-slate-500">{describeAccountAccess(r)}</div>
      </div>
      {r.role && (
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize shrink-0">{r.role}</span>
      )}
      {change(r)}
    </li>
  );

  return (
    <div className="space-y-4">
      {data.kind === "external" && (
        <p className="text-[11px] text-slate-500">Outside Joveo: they can only read published docs.</p>
      )}
      {data.kind === "service" && (
        <p className="text-[11px] text-slate-500">An API integration: it reads published, non-private docs.</p>
      )}
      {data.superAdmin && (
        <p className="text-[11px] text-slate-500">Super admin: full access to every project.</p>
      )}
      <section aria-label="Can open">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Can open ({can.length})</h4>
        {can.length
          ? <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100">{can.map(row)}</ul>
          : <p className="text-xs text-slate-400 px-1">Nothing you can see.</p>}
      </section>
      {cannot.length > 0 && (
        <section aria-label="Can't open">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Can't open ({cannot.length})</h4>
          <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100">{cannot.map(row)}</ul>
        </section>
      )}
    </div>
  );
}
